/**
 * Web Implementation for Firebase Analytics
 * Replaces the silent stub with real Firebase Web SDK functionality
 */
import { getAnalytics, isSupported, logEvent, setUserId, setUserProperties, setAnalyticsCollectionEnabled } from 'firebase/analytics';
import { initializeFirebaseWeb } from '../firebase-web/config';

let analyticsInstance: any = null;
let isAnalyticsSupported = false;
let isInitializing = false;
const eventQueue: Array<() => void> = [];

// Check if analytics is supported in this environment (e.g., prevents issues in SSR or private browsing)
if (typeof window !== 'undefined' && !isInitializing) {
  isInitializing = true;
  isSupported().then(supported => {
    isAnalyticsSupported = supported;
    if (supported) {
      try {
        const app = initializeFirebaseWeb();
        if (app) {
          analyticsInstance = getAnalytics(app);
          console.log('✅ [WEB ANALYTICS] Firebase Analytics initialized successfully');
          
          // Process queued events
          while (eventQueue.length > 0) {
            const task = eventQueue.shift();
            if (task) task();
          }
        }
      } catch (e) {
        console.warn('⚠️ [WEB ANALYTICS] Failed to initialize Firebase Analytics:', e);
      }
    } else {
      console.warn('⚠️ [WEB ANALYTICS] Firebase Analytics is not supported in this environment');
    }
  }).catch(err => {
    console.error('❌ [WEB ANALYTICS] Error checking support:', err);
  });
}

const analyticsWeb = {
  logEvent: (eventName: string, params?: any) => {
    if (analyticsInstance && isAnalyticsSupported) {
      console.log(`📊 [WEB ANALYTICS] Event: ${eventName}`, params);
      return logEvent(analyticsInstance, eventName, params);
    }
    
    if (isAnalyticsSupported || isInitializing) {
      console.log(`⏳ [WEB ANALYTICS] Queuing event: ${eventName}`);
      eventQueue.push(() => logEvent(analyticsInstance, eventName, params));
      return Promise.resolve();
    }
    
    console.log(`📽️ [WEB ANALYTICS STUB] Event: ${eventName} (Analytics not supported)`, params);
    return Promise.resolve();
  },
  setUserId: (id: string | null) => {
    if (analyticsInstance && isAnalyticsSupported) {
      console.log(`👤 [WEB ANALYTICS] Set User ID: ${id}`);
      return setUserId(analyticsInstance, id);
    }
    if (isAnalyticsSupported || isInitializing) {
      eventQueue.push(() => setUserId(analyticsInstance, id));
    }
    return Promise.resolve();
  },
  setUserProperties: (properties: any) => {
    if (analyticsInstance && isAnalyticsSupported) {
      console.log('📋 [WEB ANALYTICS] Set User Properties:', properties);
      return setUserProperties(analyticsInstance, properties);
    }
    if (isAnalyticsSupported || isInitializing) {
      eventQueue.push(() => setUserProperties(analyticsInstance, properties));
    }
    return Promise.resolve();
  },
  setAnalyticsCollectionEnabled: (enabled: boolean) => {
    if (analyticsInstance && isAnalyticsSupported) {
      console.log(`⚙️ [WEB ANALYTICS] Collection Enabled: ${enabled}`);
      return setAnalyticsCollectionEnabled(analyticsInstance, enabled);
    }
    if (isAnalyticsSupported || isInitializing) {
      eventQueue.push(() => setAnalyticsCollectionEnabled(analyticsInstance, enabled));
    }
    return Promise.resolve();
  },
  logScreenView: (params: { screen_name: string; screen_class: string }) => {
    const screenParams = {
      firebase_screen: params.screen_name,
      firebase_screen_class: params.screen_class,
      ...params
    };

    if (analyticsInstance && isAnalyticsSupported) {
      console.log(`📱 [WEB ANALYTICS] Screen View: ${params.screen_name}`);
      // In Web SDK, screen_view is a standard event
      return logEvent(analyticsInstance, 'screen_view', screenParams);
    }
    if (isAnalyticsSupported || isInitializing) {
      console.log(`⏳ [WEB ANALYTICS] Queuing Screen View: ${params.screen_name}`);
      eventQueue.push(() => logEvent(analyticsInstance, 'screen_view', screenParams));
      return Promise.resolve();
    }
    console.log(`📱 [WEB ANALYTICS STUB] Screen View: ${params.screen_name}`);
    return Promise.resolve();
  },
};

// Export as a function that returns the implementation (mimics Firebase module behavior)
export default () => analyticsWeb;
